/**
 * Image Scanner module for handling gear screenshot uploads.
 */

export function initScanner() {
    // 0. Environment check: Only enable scanner on touch devices (mobile/tablet)
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
    
    if (!isTouchDevice) {
        // Remove camera features from the DOM for non-touch (desktop) devices
        document.querySelectorAll('.camera-feature').forEach(el => el.remove());
        // We can safely return here as there are no scan buttons left to hook up
        return;
    }

    // 1. Hook up the "Scan" button to trigger the hidden file input
    document.querySelectorAll('.scan-gear-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const prefix = e.currentTarget.dataset.prefix;
            const fileInput = document.getElementById(`${prefix}_scan_input`);
            if (fileInput) fileInput.click();
        });
    });

    // 2. Listen for a file selection and process the upload
    document.querySelectorAll('input[type="file"][id$="_scan_input"]').forEach(input => {
        input.addEventListener('change', async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            
            const prefix = e.target.id.split('_')[0];
            const btn = document.querySelector(`.scan-gear-btn[data-prefix="${prefix}"]`);
            const originalHtml = btn.innerHTML;
            const originalStyle = btn.getAttribute('style') || '';
            
            // Clear any previous errors upon upload start
            const scanErrorDiv = document.getElementById(`${prefix}_scanner_error`);
            if (scanErrorDiv) {
                scanErrorDiv.style.display = 'none';
            }
            
            let scanSuccessful = false;
            let progressInterval;
            
            try {
                const computedColor = window.getComputedStyle(btn).color;
                btn.style.setProperty('color', computedColor, 'important');
                btn.style.setProperty('opacity', '1', 'important');
                btn.style.borderColor = 'transparent';
                btn.style.position = 'relative';

                // Set loading state visually (Animated 3 dots + circular progress ring)
                btn.innerHTML = `
                    <svg style="position: absolute; top: -1px; left: -1px; width: 38px; height: 38px; pointer-events: none;" viewBox="0 0 38 38" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="19" cy="19" r="17" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.3"></circle>
                        <circle id="${prefix}_progress_ring" cx="19" cy="19" r="17" fill="none" stroke="currentColor" stroke-width="3.5" 
                                stroke-linecap="round" stroke-dasharray="106.8" stroke-dashoffset="106.8" 
                                transform="rotate(-90 19 19)" style="transition: stroke-dashoffset 0.3s ease-out;"></circle>
                    </svg>
                    <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="4" cy="12" r="3"><animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0s"/></circle>
                            <circle cx="12" cy="12" r="3"><animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.2s"/></circle>
                            <circle cx="20" cy="12" r="3"><animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" begin="0.4s"/></circle>
                        </svg>
                    </div>
                `;
                btn.disabled = true;
                
                const progressCircle = document.getElementById(`${prefix}_progress_ring`);
                const circumference = 106.8;
                let startTime = Date.now();
                
                progressInterval = setInterval(() => {
                    if (!progressCircle) return;
                    const elapsed = Date.now() - startTime;
                    // Asymptotic progress towards 95% over ~20 seconds
                    const currentProgress = 95 * (1 - Math.exp(-elapsed / 12000));
                    const offset = circumference - (currentProgress / 100) * circumference;
                    progressCircle.style.strokeDashoffset = offset;
                }, 100);
                
                const formData = new FormData();
                formData.append('file', file);
                
                // Call the backend OpenCV layout processor
                const response = await fetch('/api/scan-gear', {
                    method: 'POST',
                    body: formData
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                    if (data.error) {
                        const errorDiv = document.getElementById(`${prefix}_scanner_error`);
                        if (errorDiv) {
                            errorDiv.querySelector('.error-text').textContent = data.error;
                            errorDiv.style.display = 'flex';
                            // Always reset to error aesthetics upon a full processing failure
                            errorDiv.style.color = '#e94560';
                            errorDiv.style.backgroundColor = 'rgba(233, 69, 96, 0.1)';
                            errorDiv.style.borderColor = 'rgba(233, 69, 96, 0.3)';
                        }
                        return; // Halt politely after custom user alert
                    } else {
                        throw new Error('Failed to scan image');
                    }
                }
                
                // Clear any previous errors upon success
                const errorDiv = document.getElementById(`${prefix}_scanner_error`);
                if (errorDiv) {
                    errorDiv.style.display = 'none';
                    // Reset to error aesthetics in case it was a warning previously
                    errorDiv.style.color = '#e94560';
                    errorDiv.style.backgroundColor = 'rgba(233, 69, 96, 0.1)';
                    errorDiv.style.borderColor = 'rgba(233, 69, 96, 0.3)';
                }
                
                // Populate frontend select inputs
                let foundAny = false;
                for (const [slot, item] of Object.entries(data)) {
                    if (!item) continue;
                    foundAny = true;
                    
                    // Route trinket detections into trinket1 by default so the user sees it
                    let targetSlot = slot;
                    if (slot === 'trinket') {
                        targetSlot = 'trinket1';
                    }
                    
                    if (item.name) {
                        console.log(`Processed ${targetSlot}: ${item.name} | Level: ${item.level || 'Unknown'} | Quality: ${item.quality || 'Unknown'}`);
                    }
                    
                    const nameEl = document.getElementById(`${prefix}_${targetSlot}_name`);
                    const qualEl = document.getElementById(`${prefix}_${targetSlot}_quality`);
                    const lvlEl  = document.getElementById(`${prefix}_${targetSlot}_level`);
                    
                    if (nameEl && item.name) {
                        nameEl.value = item.name;
                        nameEl.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    if (qualEl && item.quality) {
                        qualEl.value = item.quality.toLowerCase();
                        qualEl.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                    if (lvlEl && item.level) {
                        lvlEl.value = item.level;
                        lvlEl.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
                
                if (!foundAny) {
                    alert('No gear recognized. Please ensure the screenshot clearly shows the gear slots with high contrast.');
                } else {
                    scanSuccessful = true;
                    // Force progress to 100%
                    if (progressInterval) clearInterval(progressInterval);
                    if (progressCircle) {
                        progressCircle.style.strokeDashoffset = '0';
                        // Wait briefly for the progress ring to hit 100% before changing to checkmark
                        await new Promise(resolve => setTimeout(resolve, 350));
                    }
                }
                
                // Expose polite degradation warnings sent from the backend
                if (data.warning && errorDiv) {
                    errorDiv.querySelector('.error-text').textContent = data.warning;
                    errorDiv.style.display = 'flex';
                    // Swap to warning aesthetics
                    errorDiv.style.color = '#f0a500';
                    errorDiv.style.backgroundColor = 'rgba(240, 165, 0, 0.1)';
                    errorDiv.style.borderColor = 'rgba(240, 165, 0, 0.3)';
                }
                
            } catch (err) {
                console.error("Scan error:", err);
                const errorDiv = document.getElementById(`${prefix}_scanner_error`);
                if (errorDiv) {
                    errorDiv.querySelector('.error-text').textContent = "An error occurred analyzing the image.";
                    errorDiv.style.display = 'flex';
                }
            } finally {
                if (progressInterval) clearInterval(progressInterval);
                
                if (scanSuccessful) {
                    // Show a bold green check mark
                    btn.innerHTML = `
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                    `;
                    // Temporarily change button background to vibrant green
                    btn.style.backgroundColor = '#4CAF50';
                    btn.style.borderColor = '#4CAF50';
                    btn.style.boxShadow = '0 4px 10px rgba(76, 175, 80, 0.4)';
                    btn.style.color = '#fff';
                    btn.style.opacity = '1';
                    
                    setTimeout(() => {
                        btn.innerHTML = originalHtml;
                        btn.disabled = false;
                        btn.setAttribute('style', originalStyle);
                    }, 2500); // 2.5 seconds
                } else {
                    // Restore button state immediately
                    btn.innerHTML = originalHtml;
                    btn.disabled = false;
                    btn.setAttribute('style', originalStyle);
                }
                e.target.value = ''; // Reset the input to allow scanning the same file again if needed
            }
        });
    });
}
